import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import { InvoiceEvent } from '../types';
import apiService from '../services/api';

const LOG_EVENT_TYPES = new Set(['CREATED', 'SENT', 'PAID', 'VOIDED']);

const formatLogDate = (dateString: string): string => {
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const formatLogTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getLogLabel = (
  eventType: string,
  sentCountBefore: number
): string | null => {
  switch (eventType) {
    case 'CREATED':
      return 'Invoice Created';
    case 'SENT':
      return sentCountBefore === 0 ? 'Invoice Sent' : 'Invoice Resent';
    case 'PAID':
      return 'Invoice Paid';
    case 'VOIDED':
      return 'Invoice Voided';
    default:
      return null;
  }
};

type InvoiceAppointmentContext = {
  id: string;
  scheduledAt: string | null;
};

type LogEntry = {
  id: string;
  createdAt: string;
  label: string;
  appointmentId?: string;
  appointmentScheduledAt?: string | null;
};

function parseCreatedEventPayload(eventText: string): {
  appointmentId?: string;
  appointmentScheduledAt?: string;
} {
  try {
    const raw = typeof eventText === 'string' ? JSON.parse(eventText) : {};
    const appointmentId = String(raw?.appointmentId ?? '').trim() || undefined;
    const appointmentScheduledAt = String(raw?.appointmentScheduledAt ?? '').trim() || undefined;
    return { appointmentId, appointmentScheduledAt };
  } catch {
    return {};
  }
}

function resolveCreatedAppointmentContext(
  event: InvoiceEvent | undefined,
  appointmentContext?: InvoiceAppointmentContext | null
): { appointmentId?: string; appointmentScheduledAt?: string | null } {
  const parsed = event ? parseCreatedEventPayload(event.eventText) : {};
  const appointmentId = parsed.appointmentId || appointmentContext?.id;
  if (!appointmentId) return {};

  const appointmentScheduledAt =
    parsed.appointmentScheduledAt ||
    (appointmentContext?.id === appointmentId ? appointmentContext.scheduledAt : null);

  return { appointmentId, appointmentScheduledAt };
}

function buildLogEntries(
  events: InvoiceEvent[],
  invoiceCreatedAt?: string | null,
  appointmentContext?: InvoiceAppointmentContext | null
): LogEntry[] {
  const entries: LogEntry[] = [];
  let sentCount = 0;
  let hasCreatedEvent = false;

  for (const event of events) {
    if (!LOG_EVENT_TYPES.has(event.eventType)) continue;
    const label = getLogLabel(event.eventType, event.eventType === 'SENT' ? sentCount : 0);
    if (!label) continue;
    if (event.eventType === 'SENT') sentCount += 1;

    const entry: LogEntry = {
      id: event.id,
      createdAt: event.createdAt,
      label,
    };

    if (event.eventType === 'CREATED') {
      hasCreatedEvent = true;
      const apt = resolveCreatedAppointmentContext(event, appointmentContext);
      if (apt.appointmentId) {
        entry.appointmentId = apt.appointmentId;
        entry.appointmentScheduledAt = apt.appointmentScheduledAt ?? null;
      }
    }

    entries.push(entry);
  }

  if (!hasCreatedEvent && invoiceCreatedAt) {
    const apt = resolveCreatedAppointmentContext(undefined, appointmentContext);
    entries.unshift({
      id: 'fallback-created',
      createdAt: invoiceCreatedAt,
      label: 'Invoice Created',
      appointmentId: apt.appointmentId,
      appointmentScheduledAt: apt.appointmentScheduledAt ?? null,
    });
  }

  return entries.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

const appointmentLinkClassName = 'text-[#0B4778] font-medium hover:underline';

const CreatedLogDetail: React.FC<{
  appointmentId: string;
  appointmentScheduledAt?: string | null;
}> = ({ appointmentId, appointmentScheduledAt }) => (
  <>
    {' '}
    from{' '}
    <Link to={`/appointments/${appointmentId}`} className={appointmentLinkClassName}>
      Appointment
    </Link>
    {appointmentScheduledAt ? (
      <> for {formatLogDate(appointmentScheduledAt)} @ {formatLogTime(appointmentScheduledAt)}</>
    ) : null}
  </>
);

const LogLineDetail: React.FC<{ entry: LogEntry }> = ({ entry }) => {
  if (entry.label === 'Invoice Created' && entry.appointmentId) {
    return (
      <>
        {entry.label}
        <CreatedLogDetail
          appointmentId={entry.appointmentId}
          appointmentScheduledAt={entry.appointmentScheduledAt}
        />
      </>
    );
  }
  return <>{entry.label}</>;
};

export interface InvoiceLogsSectionProps {
  invoiceId: string | undefined;
  /** Fallback when no CREATED event exists (e.g. invoices predating invoice_events). */
  invoiceCreatedAt?: string | null;
  /** When invoice was created from an appointment. */
  appointmentContext?: InvoiceAppointmentContext | null;
  refreshTrigger?: number;
}

const InvoiceLogsSection: React.FC<InvoiceLogsSectionProps> = ({
  invoiceId,
  invoiceCreatedAt,
  appointmentContext,
  refreshTrigger = 0,
}) => {
  const [events, setEvents] = useState<InvoiceEvent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!invoiceId) {
      setEvents([]);
      return;
    }
    setLoading(true);
    apiService
      .getInvoiceEvents(invoiceId)
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [invoiceId, refreshTrigger]);

  const logEntries = useMemo(
    () => buildLogEntries(events, invoiceCreatedAt, appointmentContext),
    [events, invoiceCreatedAt, appointmentContext]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <History className="w-5 h-5 text-gray-600" />
        Logs
      </h2>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-1">
          <span
            className="inline-block w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"
            aria-hidden
          />
          Loading logs…
        </div>
      ) : logEntries.length === 0 ? (
        <p className="text-sm text-gray-500">No logs yet.</p>
      ) : (
        <ul className="space-y-2">
          {logEntries.map((entry) => (
            <li key={entry.id} className="text-sm text-gray-700">
              <span className="font-semibold text-gray-900">{formatLogDate(entry.createdAt)}</span>
              {' — '}
              <LogLineDetail entry={entry} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default InvoiceLogsSection;
